import { getCoverDisplayUrl } from '../../utils/coverUrl';
import React from 'react';
import { Link } from 'react-router-dom';
import { Headphones } from 'lucide-react';
import BookViewMetrics from '../BookViewMetrics';

const getCoverUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${window.location.origin}${url}`;
  return url;
};

const HeroHeader = ({ book, children }) => {
  const hasAudio = book.audiobook_file_url || book.audiobook_chapters?.length > 0;
  return (
    <div className="relative h-full max-w-screen-2xl mx-auto px-8 pt-32 pb-12">
      <div className="flex flex-col md:flex-row items-start gap-8 w-full">
        <div className="flex-shrink-0">
          <img
            src={getCoverDisplayUrl(book.cover_image_url || book.coverImage)}
            alt={book.title}
            className="w-64 h-96 object-cover rounded-lg shadow-2xl"
          />
        </div>
        <div className="flex-1 space-y-6">
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight" data-testid="book-title">
            {book.title}
          </h1>
          <p className="text-xl text-gray-300 font-medium">
            by{' '}
            <Link
              to={`/author/${encodeURIComponent((book.author || '').trim())}`}
              className="hover:text-red-300 underline-offset-4 hover:underline transition-colors"
              data-testid="author-profile-link"
            >
              {book.author}
            </Link>
          </p>
          <div className="flex flex-wrap items-center gap-4 text-white">
            <div className="flex items-center space-x-2 text-lg" data-testid="book-rating">
              <span className="text-yellow-400">★</span>
              <span className="font-semibold">{book.rating?.toFixed(1) || '0.0'}</span>
              <span className="text-gray-400 text-sm">({book.total_reviews || 0} reviews)</span>
            </div>
            <span className="text-gray-400">•</span>
            <span className="text-lg">{book.publish_year}</span>
            <span className="text-gray-400">•</span>
            <span className="text-lg">{book.pages} pages</span>
            {book.book_file_url && (
              <>
                <span className="text-gray-400">•</span>
                <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full font-semibold">
                  ✓ Full Book Available
                </span>
              </>
            )}
            {hasAudio && (
              <>
                <span className="text-gray-400">•</span>
                <span className="px-3 py-1 bg-purple-600 text-white text-sm rounded-full font-semibold flex items-center">
                  <Headphones className="h-3 w-3 mr-1" />
                  Audiobook Available
                </span>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {book.genres?.map((genre, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm border border-white/20"
              >
                {genre}
              </span>
            ))}
          </div>
          <p className="text-lg text-gray-200 leading-relaxed max-w-3xl">{book.description}</p>
          <BookViewMetrics bookId={book.id} />
          {children}
        </div>
      </div>
    </div>
  );
};

export default HeroHeader;
